import React, { useState, useCallback } from 'react';
import {
  User,
  Mail,
  Phone,
  Building2,
  MessageSquare,
  CheckCircle,
  ArrowRight,
  Loader2,
  Linkedin,
} from 'lucide-react';
import axios from 'axios';
import { useGoogleLogin } from '@react-oauth/google';
import config from './config/config';

/* ── Design-system CSS ─────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600&family=Space+Mono:wght@400;700&family=Poppins:wght@300;400;500&display=swap');

/* root */
.wl-root {
  position: relative;
  background: #0d0d0d;
  min-height: 100vh;
  overflow: hidden;
  font-family: 'Poppins', sans-serif;
  font-weight: 300;
}
/* 40 px grid texture */
.wl-root::before {
  content: '';
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,.022) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.022) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none; z-index: 0;
}
/* ambient glows — one orange TR, one grey BL */
.wl-glow-tr {
  position: absolute; top: -80px; right: -80px;
  width: 420px; height: 420px; border-radius: 50%;
  background: radial-gradient(circle, rgba(225,100,46,.07) 0%, transparent 65%);
  pointer-events: none; z-index: 0;
}
.wl-glow-bl {
  position: absolute; bottom: -60px; left: -60px;
  width: 300px; height: 300px; border-radius: 50%;
  background: radial-gradient(circle, rgba(114,115,117,.04) 0%, transparent 65%);
  pointer-events: none; z-index: 0;
}

/* nav */
.wl-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 100;
  padding: 16px 48px;
  display: flex; align-items: center; justify-content: space-between;
  background: rgba(13,13,13,.88);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255,255,255,.07);
}
.wl-nav-links { display: flex; align-items: center; gap: 18px; }
.wl-nav-link {
  display: flex; align-items: center; gap: 7px;
  padding: 4px 0;
  border: none; background: none;
  text-decoration: none;
  font-family: 'Space Grotesk', sans-serif; font-size: 12px; font-weight: 400;
  color: #a0a0a0;
  transition: color .2s;
}
.wl-nav-link:hover { color: #f4f4f4; }
@media (max-width: 560px) {
  .wl-nav { padding: 14px 20px; }
  .wl-nav-link span { display: none; }
  .wl-nav-link { padding: 7px 10px; }
}

/* inner */
.wl-inner {
  position: relative; z-index: 1;
  display: flex; flex-direction: column; align-items: center;
  padding: 120px 24px 80px;
  width: 100%;
}

/* eyebrow — orange dash + ALL CAPS label */
.wl-eyebrow {
  font-family: 'Space Mono', monospace;
  font-size: 9px; color: #a0a0a0;
  letter-spacing: .15em; text-transform: uppercase;
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 22px;
  animation: wl-up .5s ease both;
}
.wl-eyebrow::before {
  content: ''; width: 20px; height: 1px;
  background: #e1642e; flex-shrink: 0;
}
/* pulsing live dot beside eyebrow */
.wl-eyebrow-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: #e1642e;
  box-shadow: 0 0 5px rgba(225,100,46,.8);
  animation: wl-pulse 2s ease infinite;
  flex-shrink: 0;
}
@keyframes wl-pulse { 0%,100% { opacity: 1; } 50% { opacity: .4; } }

/* headline */
.wl-h1 {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(36px, 5vw, 58px);
  font-weight: 400; color: #f4f4f4;
  line-height: 1.06; letter-spacing: -.025em;
  text-align: center; margin-bottom: 16px;
  animation: wl-up .6s .08s ease both;
}
.wl-accent { color: #e1642e; }

/* body */
.wl-body {
  font-family: 'Poppins', sans-serif;
  font-size: 14px; font-weight: 300; color: #b0b0b0;
  line-height: 1.8; text-align: center;
  max-width: 420px; margin-bottom: 36px;
  animation: wl-up .6s .16s ease both;
}

/* form card wrapper */
.wl-card-wrap {
  width: 100%; max-width: 440px;
  animation: wl-up .6s .24s ease both;
}

/* card — solid background so grid texture doesn't bleed through */
.wl-card {
  position: relative;
  background: #161616;
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 5px; overflow: hidden;
}
/* orange top hairline */
.wl-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(225,100,46,.55), transparent);
  pointer-events: none;
}
.wl-card-body  { padding: 32px 32px 26px; display: flex; flex-direction: column; gap: 14px; }
.wl-card-foot  { padding: 14px 32px; border-top: 1px solid rgba(255,255,255,.07); background: rgba(0,0,0,.15); }

/* Google button */
.wl-btn-google {
  width: 100%; display: flex; align-items: center; justify-content: center; gap: 10px;
  padding: 11px 20px;
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 3px; cursor: pointer;
  color: #f4f4f4;
  font-family: 'Space Grotesk', sans-serif; font-size: 13px; font-weight: 400;
  transition: border-color .2s, background .2s;
}
.wl-btn-google:hover { border-color: rgba(255,255,255,.18); background: rgba(255,255,255,.06); }

/* divider */
.wl-divider { display: flex; align-items: center; }
.wl-divider::before,.wl-divider::after {
  content: ''; flex: 1; height: 1px; background: rgba(255,255,255,.07);
}
.wl-divider-txt {
  font-family: 'Space Mono', monospace;
  font-size: 9px; color: #5a5a5a;
  letter-spacing: .1em; text-transform: uppercase; padding: 0 12px;
}

/* inputs */
.wl-field { position: relative; }
.wl-input,.wl-textarea {
  width: 100%; box-sizing: border-box;
  padding: 11px 13px 11px 40px;
  background: rgba(255,255,255,.03);
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 3px;
  color: #f4f4f4;
  font-family: 'Poppins', sans-serif; font-size: 13px; font-weight: 300;
  outline: none;
  transition: border-color .2s, background .2s;
}
.wl-input::placeholder,.wl-textarea::placeholder { color: #6a6a6a; }
.wl-input:hover,.wl-textarea:hover { border-color: rgba(255,255,255,.13); }
.wl-input:focus,.wl-textarea:focus { border-color: rgba(225,100,46,.4); background: rgba(225,100,46,.025); }
.wl-input.wl-err { border-color: rgba(239,68,68,.45); }
.wl-textarea { resize: none; padding-top: 11px; }

.wl-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  width: 15px; height: 15px; color: #5a5a5a;
  transition: color .2s; pointer-events: none;
}
.wl-icon-top { position: absolute; left: 12px; top: 13px; width: 15px; height: 15px; color: #5a5a5a; pointer-events: none; }
.wl-field:focus-within .wl-icon,
.wl-field:focus-within .wl-icon-top { color: #e1642e; }

/* primary submit */
.wl-btn-primary {
  width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px 22px;
  background: #e1642e; color: #fff;
  font-family: 'Space Grotesk', sans-serif; font-size: 13px; font-weight: 500;
  border: none; border-radius: 3px; cursor: pointer;
  position: relative; overflow: hidden;
  transition: opacity .2s, transform .2s, box-shadow .25s;
  box-shadow: 0 4px 14px rgba(225,100,46,.18);
}
.wl-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(225,100,46,.32); }
.wl-btn-primary:disabled { opacity: .5; cursor: not-allowed; }
.wl-btn-primary::before {
  content: ''; position: absolute;
  top: 0; left: -100%; width: 100%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.18), transparent);
  transition: left .55s;
}
.wl-btn-primary:hover:not(:disabled)::before { left: 100%; }

/* ghost (skip / secondary) */
.wl-btn-ghost {
  flex: 1; display: flex; align-items: center; justify-content: center;
  padding: 10px 18px;
  background: transparent; color: #8a8a8a;
  font-family: 'Space Grotesk', sans-serif; font-size: 12px;
  border: 1px solid rgba(255,255,255,.1); border-radius: 3px; cursor: pointer;
  transition: border-color .25s, color .25s;
}
.wl-btn-ghost:hover { border-color: rgba(255,255,255,.2); color: #f4f4f4; }

/* small submit (update-info) */
.wl-btn-sm {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 18px;
  background: #e1642e; color: #fff;
  font-family: 'Space Grotesk', sans-serif; font-size: 12px; font-weight: 500;
  border: none; border-radius: 3px; cursor: pointer;
  transition: opacity .2s, transform .2s;
}
.wl-btn-sm:hover:not(:disabled) { transform: translateY(-1px); }
.wl-btn-sm:disabled { opacity: .5; cursor: not-allowed; }

/* error / hint */
.wl-err-txt { font-family: 'Poppins', sans-serif; font-size: 11px; color: #ef4444; margin-top: 5px; }
.wl-hint {
  display: flex; align-items: flex-start; gap: 5px; margin-top: 5px;
  font-family: 'Poppins', sans-serif; font-size: 11px; font-weight: 300; color: #727375; line-height: 1.5;
}
.wl-hint-star { color: #e1642e; font-weight: 700; flex-shrink: 0; }

/* footnote */
.wl-footnote {
  font-family: 'Space Mono', monospace;
  font-size: 8.5px; color: #888;
  text-align: center; letter-spacing: .09em; text-transform: uppercase;
}

/* social strip */
.wl-social-strip {
  position: relative; width: 100%; max-width: 620px; display: flex;
  background: rgba(255,255,255,.022);
  border: 1px solid rgba(255,255,255,.07);
  border-radius: 5px; overflow: hidden;
  margin-top: 12px;
  animation: wl-up .6s .36s ease both;
}
.wl-social-strip::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent 10%, rgba(225,100,46,.5) 50%, transparent 90%);
  pointer-events: none;
}
.wl-sc-card {
  flex: 1; display: flex; align-items: center; gap: 12px;
  padding: 14px 20px; text-decoration: none;
  border-right: 1px solid rgba(255,255,255,.06);
  transition: background .2s;
}
.wl-sc-card:last-child { border-right: none; }
.wl-sc-card:hover { background: rgba(255,255,255,.03); }
.wl-sc-icon {
  width: 30px; height: 30px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  border-radius: 4px; border: 1px solid transparent;
  transition: transform .2s;
}
.wl-sc-card:hover .wl-sc-icon { transform: scale(1.08); }
.wl-sc-icon.dc { background: rgba(88,101,242,.1); border-color: rgba(88,101,242,.18); color: #7289da; }
.wl-sc-icon.li { background: rgba(10,102,194,.1); border-color: rgba(10,102,194,.18); color: #4d9fd6; }
.wl-sc-icon.x  { background: rgba(255,255,255,.06); border-color: rgba(255,255,255,.12); color: #e0e0e0; }
.wl-sc-name { font-family: 'Space Grotesk', sans-serif; font-size: 11px; font-weight: 500; color: #f4f4f4; margin-bottom: 2px; }
.wl-sc-name strong { color: #e1642e; font-weight: 500; }
.wl-sc-desc { font-family: 'Space Mono', monospace; font-size: 8px; color: #999; letter-spacing: .06em; text-transform: uppercase; }
.wl-sc-arrow { font-size: 14px; color: #5a5a5a; margin-left: auto; flex-shrink: 0; transition: color .2s, transform .2s; }
.wl-sc-card:hover .wl-sc-arrow { color: #e1642e; transform: translateX(3px); }

/* success state */
.wl-success-icon {
  width: 44px; height: 44px; border-radius: 5px;
  background: rgba(225,100,46,.07); border: 1px solid rgba(225,100,46,.18);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 10px;
}
.wl-success-h {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 19px; font-weight: 400; color: #f4f4f4;
  text-align: center; margin-bottom: 4px;
}
.wl-success-sub {
  font-family: 'Poppins', sans-serif; font-size: 13px; font-weight: 300; color: #c0c0c0;
  text-align: center; margin-bottom: 0;
}

/* section divider inside card */
.wl-section-sep { border: none; border-top: 1px solid rgba(255,255,255,.07); margin: 0; }

/* fade-up keyframe */
@keyframes wl-up {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes spin { to { transform: rotate(360deg); } }

/* subtle drifting orbs */
@keyframes wl-drift-a {
  0%,100% { transform: translate(0,0) scale(1); opacity: .55; }
  50%      { transform: translate(-24px, 18px) scale(1.08); opacity: .75; }
}
@keyframes wl-drift-b {
  0%,100% { transform: translate(0,0) scale(1); opacity: .4; }
  50%      { transform: translate(20px, -16px) scale(1.06); opacity: .6; }
}
.wl-orb-a {
  position: absolute; border-radius: 50%; pointer-events: none; z-index: 0;
  top: -100px; right: -60px; width: 480px; height: 480px;
  background: radial-gradient(circle, rgba(225,100,46,.07) 0%, transparent 65%);
  animation: wl-drift-a 18s ease-in-out infinite;
}
.wl-orb-b {
  position: absolute; border-radius: 50%; pointer-events: none; z-index: 0;
  bottom: -80px; left: -60px; width: 360px; height: 360px;
  background: radial-gradient(circle, rgba(225,100,46,.045) 0%, transparent 65%);
  animation: wl-drift-b 24s ease-in-out infinite;
}

/* tagline */

/* responsive */
@media (max-width: 560px) {
  .wl-nav { padding: 14px 20px; }
  .wl-inner { padding: 96px 16px 60px; }
  .wl-card-body { padding: 22px 18px 18px; }
  .wl-card-foot { padding: 12px 18px; }
  .wl-social-strip { flex-direction: column; }
  .wl-sc-card { border-right: none; border-bottom: 1px solid rgba(255,255,255,.06); }
  .wl-sc-card:last-child { border-bottom: none; }
}
`;

/* ── SVG icons ──────────────────────────────────────────────────────────── */
function DiscordSVG() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}
function LinkedInSVG() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );
}

/* ── Helpers ────────────────────────────────────────────────────────────── */
function isValidPhone(value) {
  if (!value || !value.trim()) return true;
  return value.replace(/\D/g, '').length >= 10;
}

/* ── AdditionalInfoForm ─────────────────────────────────────────────────── */
const AdditionalInfoForm = ({ email, initialName = '', onSubmit, isSubmitting, submitError }) => {
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [message, setMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const handlePhoneChange = (e) => {
    const v = e.target.value;
    setPhone(v);
    if (phoneError && v.trim()) setPhoneError(isValidPhone(v) ? '' : 'Enter a valid phone number (at least 10 digits)');
    else if (phoneError) setPhoneError('');
  };
  const handlePhoneBlur = () => {
    if (phone.trim() && !isValidPhone(phone)) setPhoneError('Enter a valid phone number (at least 10 digits)');
    else setPhoneError('');
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (phone.trim() && !isValidPhone(phone)) { setPhoneError('Enter a valid phone number (at least 10 digits)'); return; }
    setPhoneError('');
    onSubmit({ email, name, phone, company, linkedin, message });
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="wl-field">
        <User className="wl-icon" />
        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Full Name (Optional)" className="wl-input" />
      </div>
      <div className="wl-field">
        <Phone className="wl-icon" />
        <input type="tel" value={phone} onChange={handlePhoneChange} onBlur={handlePhoneBlur} placeholder="Phone Number (Optional)" className={`wl-input${phoneError ? ' wl-err' : ''}`} />
        {phoneError && <p className="wl-err-txt">{phoneError}</p>}
      </div>
      <div className="wl-field">
        <Building2 className="wl-icon" />
        <input type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="Company Name (Optional)" className="wl-input" />
      </div>
      <div className="wl-field">
        <div className="wl-field">
          <Linkedin className="wl-icon" />
          <input type="text" value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="LinkedIn Profile URL (Optional)" className="wl-input" />
        </div>
        <div className="wl-hint">
          <span className="wl-hint-star">*</span>
          <span>Applicants who include their LinkedIn profile receive <strong style={{ color: '#e1642e', fontWeight: 400 }}>priority consideration</strong> for beta access.</span>
        </div>
      </div>
      <div className="wl-field">
        <MessageSquare className="wl-icon-top" />
        <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Tell us about your use case (Optional)" rows={3} className="wl-textarea" style={{ paddingLeft: 40 }} />
      </div>
      {submitError && <p className="wl-err-txt" style={{ textAlign: 'center' }}>{submitError}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
        <button type="button" onClick={() => window.location.href = 'https://www.latentforce.ai/'} className="wl-btn-ghost">Skip →</button>
        <button type="submit" disabled={isSubmitting} className="wl-btn-sm">
          {isSubmitting ? <><Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Saving…</> : 'Submit Info →'}
        </button>
      </div>
    </form>
  );
};

/* ── Main component ─────────────────────────────────────────────────────── */
/* ── Shared background shell — defined at module level so its identity is
   stable across re-renders. Defining it inside WaitingListForm would cause
   React to unmount/remount the entire tree on every keystroke. ── */
const Shell = ({ children }) => (
  <div className="wl-root">
    <style dangerouslySetInnerHTML={{ __html: CSS }} />
    <div className="wl-orb-a" />
    <div className="wl-orb-b" />
    <nav className="wl-nav">
      <a href="https://www.latentforce.ai/" target="_blank" rel="noopener noreferrer">
        <img src="/latentforce-logo.png" alt="LatentForce" style={{ height: 34, width: 'auto', objectFit: 'contain', display: 'block' }} />
      </a>
      <div className="wl-nav-links">
        <a href="https://discord.com/invite/WHF6jU287" className="wl-nav-link" target="_blank" rel="noopener noreferrer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#7289da"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
          <span>Discord</span>
        </a>
        <a href="https://www.linkedin.com/company/latentforce-ai/posts/?feedView=all" className="wl-nav-link" target="_blank" rel="noopener noreferrer">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="#4d9fd6"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          <span>LinkedIn</span>
        </a>
        <a href="https://x.com/LatentForceHQ" className="wl-nav-link" target="_blank" rel="noopener noreferrer">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          <span>X</span>
        </a>
      </div>
    </nav>
    {children}
  </div>
);

const WaitingListForm = () => {
  const [email, setEmail] = useState('');
  const [signedUpUserName, setSignedUpUserName] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isUpdatingInfo, setIsUpdatingInfo] = useState(false);
  const [additionalInfoError, setAdditionalInfoError] = useState('');
  const [additionalInfoSubmitted, setAdditionalInfoSubmitted] = useState(false);

  const handleGoogleToken = useCallback(async (tokenResponse) => {
    const accessToken = tokenResponse?.access_token;
    if (!accessToken) { setErrors(p => ({ ...p, submit: 'Google did not return an access token. Try again or use the form.' })); return; }
    setIsGoogleSubmitting(true);
    setErrors(p => ({ ...p, submit: '' }));
    try {
      const response = await axios.post(`${config.API_BASE}/api/latentforce/google-signup`, { accessToken }, { headers: { 'Content-Type': 'application/json' }, timeout: 15000 });
      if (response.status === 200) {
        const returnedEmail = response.data?.email;
        const returnedName = response.data?.name;
        if (returnedEmail) setEmail(returnedEmail);
        if (returnedName) setSignedUpUserName(returnedName);
        setIsSubmitted(true);
      }
    } catch (error) {
      const statusCode = error.response?.status;
      const serverMessage = error.response?.data?.error;
      let msg = 'Google signup failed. Please try the form below.';
      if (statusCode === 409) msg = 'This Google account is already registered.';
      else if (statusCode === 400) msg = serverMessage || 'Invalid Google sign-in. Try again.';
      else if (serverMessage) msg = serverMessage;
      else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) msg = 'Request timed out. Check your connection and try again.';
      else if (error.message === 'Network Error') msg = 'Network error. Check your connection and that the server is reachable.';
      setErrors(p => ({ ...p, submit: msg }));
    } finally { setIsGoogleSubmitting(false); }
  }, []);

  const startGoogleLogin = useGoogleLogin({
    flow: 'implicit', scope: 'openid email profile', ux_mode: 'popup',
    onSuccess: handleGoogleToken,
    onError: () => setErrors(p => ({ ...p, submit: 'Google sign-in was cancelled or failed. Try again or use the form below.' })),
  });

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const emailError = !email.trim() ? 'Email is required' : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'Please enter a valid email' : '';
    if (emailError) { setErrors({ email: emailError }); return; }
    setIsSubmitting(true); setErrors({});
    try {
      await axios.post(`${config.API_BASE}/api/waitlist/email-only`, { email });
      setIsSubmitted(true);
    } catch (error) {
      const msg = error.response?.status === 409 ? 'This email is already on our waiting list.' : error.response?.data?.error || 'Something went wrong. Please try again.';
      setErrors({ submit: msg });
    } finally { setIsSubmitting(false); }
  };

  const handleAdditionalInfoSubmit = useCallback(async (data) => {
    setIsUpdatingInfo(true); setAdditionalInfoError('');
    try {
      await axios.post(`${config.API_BASE}/api/waitlist/update-info`, { email, ...data });
      setAdditionalInfoSubmitted(true);
    } catch (error) {
      console.error('Update info error:', error);
      setAdditionalInfoError('Failed to update info. You can skip this step.');
    } finally { setIsUpdatingInfo(false); }
  }, [email]);

  /* ── Success state ── */
  if (isSubmitted) {
    return (
      <Shell>
        <div className="wl-inner">
          <div className="wl-card-wrap">
            <div className="wl-card">
              <div className="wl-card-body">
                {additionalInfoSubmitted ? (
                  <div style={{ textAlign: 'center', padding: '8px 0' }}>
                    <div className="wl-success-icon">
                      <CheckCircle size={24} color="#e1642e" strokeWidth={1.5} />
                    </div>
                    <h2 className="wl-success-h">Thank you.</h2>
                    <p className="wl-success-sub">We'll be in touch soon</p>
                    <p style={{ fontFamily: 'Poppins', fontSize: 13, fontWeight: 300, color: '#8a8a8a', lineHeight: 1.7 }}>
                      Your input helps us build something you'll love. In the meantime, explore{' '}
                      <a href="https://www.latentforce.ai/" style={{ color: '#e1642e', textDecoration: 'none' }}>latentforce.ai</a>.
                    </p>
                  </div>
                ) : (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: 10 }}>
                      <div className="wl-success-icon">
                        <CheckCircle size={22} color="#e1642e" strokeWidth={1.5} />
                      </div>
                      <h2 className="wl-success-h">You're on the list.</h2>
                      <p className="wl-success-sub">Confirmation sent to <span style={{ color: '#e1642e', fontWeight: 400 }}>{email}</span></p>
                    </div>
                    <hr className="wl-section-sep" />
                    <div className="wl-eyebrow" style={{ margin: '10px 0 10px', justifyContent: 'center' }}>Help us serve you better</div>
                    <AdditionalInfoForm
                      email={email}
                      initialName={signedUpUserName}
                      onSubmit={handleAdditionalInfoSubmit}
                      isSubmitting={isUpdatingInfo}
                      submitError={additionalInfoError}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  /* ── Main signup form ── */
  return (
    <Shell>
      <div className="wl-inner">
        {/* eyebrow */}
        <div className="wl-eyebrow">
          <span className="wl-eyebrow-dot" />
          Early Access — Limited Spots
        </div>

        {/* headline */}
        <h1 className="wl-h1">
          Claim Your<br />
          <span className="wl-accent">Beta Access.</span>
        </h1>

        {/* body */}
        <p className="wl-body">
          Limited beta invites. Be among the first to experience the AI context layer for your codebase.
        </p>

        {/* form card */}
        <div className="wl-card-wrap">
          <div className="wl-card">
            <div className="wl-card-body">
              <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Google */}
                {isGoogleSubmitting ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '11px 0', color: '#8a8a8a', fontFamily: 'Space Grotesk', fontSize: 13 }}>
                    <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite', color: '#e1642e' }} />
                    Signing up with Google…
                  </div>
                ) : (
                  <button type="button" onClick={() => startGoogleLogin()} className="wl-btn-google">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                  </button>
                )}

                {/* divider */}
                <div className="wl-divider">
                  <span className="wl-divider-txt">or</span>
                </div>

                {/* email */}
                <div className="wl-field">
                  <Mail className="wl-icon" />
                  <input
                    type="email" value={email}
                    onChange={e => { setEmail(e.target.value); if (errors.email) setErrors(p => ({ ...p, email: '' })); }}
                    placeholder="your@email.com"
                    className={`wl-input${errors.email ? ' wl-err' : ''}`}
                  />
                  {errors.email && <p className="wl-err-txt">{errors.email}</p>}
                </div>

                {errors.submit && <p className="wl-err-txt" style={{ textAlign: 'center' }}>{errors.submit}</p>}

                {/* submit */}
                <button type="submit" disabled={isSubmitting} className="wl-btn-primary">
                  {isSubmitting
                    ? <><Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} />Submitting…</>
                    : <>Reserve My Spot <ArrowRight size={14} /></>
                  }
                </button>
              </form>
            </div>
            <div className="wl-card-foot">
              <p className="wl-footnote">No spam &middot; unsubscribe anytime</p>
            </div>
          </div>

          {/* social strip */}
          <div className="wl-social-strip">
            <a href="https://discord.com/invite/WHF6jU287" className="wl-sc-card" target="_blank" rel="noopener noreferrer">
              <span className="wl-sc-icon dc"><DiscordSVG /></span>
              <span>
                <div className="wl-sc-name"><strong>Discord</strong> Community</div>
                <div className="wl-sc-desc">Questions · updates</div>
              </span>
              <span className="wl-sc-arrow">›</span>
            </a>
            <a href="https://www.linkedin.com/company/latentforce-ai/posts/?feedView=all" className="wl-sc-card" target="_blank" rel="noopener noreferrer">
              <span className="wl-sc-icon li"><LinkedInSVG /></span>
              <span>
                <div className="wl-sc-name"><strong>LinkedIn</strong> Updates</div>
                <div className="wl-sc-desc">News · insights</div>
              </span>
              <span className="wl-sc-arrow">›</span>
            </a>
            <a href="https://x.com/LatentForceHQ" className="wl-sc-card" target="_blank" rel="noopener noreferrer">
              <span className="wl-sc-icon x">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </span>
              <span>
                <div className="wl-sc-name"><strong>X</strong> / Twitter</div>
                <div className="wl-sc-desc">Latest posts</div>
              </span>
              <span className="wl-sc-arrow">›</span>
            </a>
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default WaitingListForm;
